import { 
  PutObjectCommand, 
  S3Client,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand
} from '@aws-sdk/client-s3';
import React, { useContext, useRef, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Col,
  Row,
  Spinner,
  Card,
  Container,
  ProgressBar
} from 'react-bootstrap';
import { storesContext } from '../../../stores/storesContext';
import PageTitle from '../../helpers/PageTitle';
import GreenAtlasBundleManager from './GreenAtlasBundleManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faFileZipper,
  faTrashAlt,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

export const GreenAtlasUploadPage = observer(() => {
  const userStore = useContext(storesContext);
  const { propertiesStore } = userStore;
  const [isPropertyLoaded, setIsPropertyLoaded] = useState(false);

  useEffect(() => {
    setIsPropertyLoaded(!!propertiesStore.selectedProperty);
  }, [propertiesStore.selectedProperty]);

  return (
    <Container fluid className='mb-5'>
      <Row className='mb-4'>
        <Col>
          <PageTitle title={'Green Atlas Viewer Bundle Upload'} />
          <h6 className='text-center text-muted'>
            Upload Green Atlas Viewer bundles for the selected property.
          </h6>
        </Col>
      </Row>

      {!isPropertyLoaded ? (
        <Row className='justify-content-center my-5'>
          <Col xs={12} className='text-center'>
            <Spinner animation='border' role='status' variant='primary' />
            <p className='mt-3'>Loading property data...</p>
          </Col>
        </Row>
      ) : (
        <>
          <Row className='my-4 justify-content-center'>
            <Col xs={12} lg={10} xl={8}>
              <Alert variant='info' className='shadow-sm'>
                <Alert.Heading>
                  <FontAwesomeIcon icon={faInfoCircle} className='me-2' /> Usage
                  Instructions:
                </Alert.Heading>
                <p>
                  To get started, click 'Browse' to select your Green Atlas ZIP
                  file, and click 'Upload'.
                </p>
                <p>
                  Files will be uploaded for the selected property and season.
                  If multiple files are uploaded, they will all be associated
                  with the current property.
                </p>
                <hr />
                <h5>File Type</h5>
                <p className='mb-0'>
                  <FontAwesomeIcon icon={faFileZipper} className='me-2' /> Only
                  Green Atlas Viewer ZIP file exports will be processed.
                </p>
              </Alert>
            </Col>
          </Row>

          <Row className='justify-content-center mb-5'>
            <Col xs={12} lg={10} xl={8}>
              <FileUpload />
            </Col>
          </Row>

          <Row className='mt-5'>
            <Col>
              <Card className='mb-3 shadow'>
                <Card.Header className='bg-light'>
                  <h4 className='text-center mb-0'>Manage Uploaded Bundles</h4>
                </Card.Header>
                <Card.Body>
                  <Card.Text className='text-center text-muted mb-4'>
                    View and manage Green Atlas bundles that have been uploaded
                    for this property.
                  </Card.Text>
                  <GreenAtlasBundleManager />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
});

const FileUpload = observer(() => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [detailedProgress, setDetailedProgress] = useState({});
  const userStore = useContext(storesContext);

  const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB
  const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileRemove = (filename) => {
    setFiles((prev) => prev.filter((f) => f.name !== filename));
  };

  const uploadToS3 = async (file, fileIndex) => {
    // Get file upload params.
    const season = userStore.selectedDatabase
      ? userStore.selectedDatabase.substr(9)
      : null;
    const property_identifier =
      userStore.propertiesStore.selectedProperty?.identifier;
    const tempAwsCredentials = userStore.tempAwsCredentials;
    const s3Client = new S3Client({
      region: 'ap-southeast-2',
      credentials: {
        accessKeyId: tempAwsCredentials['key'],
        secretAccessKey: tempAwsCredentials['secret'],
        sessionToken: tempAwsCredentials['token']
      },
      forcePathStyle: true
    });

    const key = `unprocessed/${file.name}`;
    const metadata = {
      property_identifier: property_identifier,
      season: season
    };

    try {
      if (file.size < MULTIPART_THRESHOLD) {
        // Use single-part upload for smaller files
        const params = {
          Bucket: process.env.REACT_APP_GA_BUNDLE_S3_BUCKET,
          Key: key,
          Body: file,
          ContentType: file.type,
          Metadata: metadata
        };

        const command = new PutObjectCommand(params);
        await s3Client.send(command);
        console.log(`Successfully uploaded ${file.name} to S3 (single-part)`);
      } else {
        // Use multipart upload for larger files
        await uploadMultipart(s3Client, file, key, metadata, fileIndex);
      }
    } catch (error) {
      console.error(`Error uploading ${file.name} to S3:`, error);
      throw error;
    }
  };

  const uploadMultipart = async (s3Client, file, key, metadata, fileIndex) => {
    let uploadId;
    
    try {
      // Step 1: Create multipart upload
      const createParams = {
        Bucket: process.env.REACT_APP_GA_BUNDLE_S3_BUCKET,
        Key: key,
        ContentType: file.type,
        Metadata: metadata
      };

      const createCommand = new CreateMultipartUploadCommand(createParams);
      const createResult = await s3Client.send(createCommand);
      uploadId = createResult.UploadId;

      // Step 2: Upload parts
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadedParts = [];

      for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const uploadPartParams = {
          Bucket: process.env.REACT_APP_GA_BUNDLE_S3_BUCKET,
          Key: key,
          PartNumber: partNumber,
          UploadId: uploadId,
          Body: chunk
        };

        const uploadPartCommand = new UploadPartCommand(uploadPartParams);
        const uploadPartResult = await s3Client.send(uploadPartCommand);

        uploadedParts.push({
          ETag: uploadPartResult.ETag,
          PartNumber: partNumber
        });

        // Update detailed progress for this file
        const partProgress = Math.round((partNumber / totalChunks) * 100);
        setDetailedProgress(prev => ({
          ...prev,
          [fileIndex]: {
            fileName: file.name,
            progress: partProgress,
            totalParts: totalChunks,
            uploadedParts: partNumber
          }
        }));
      }

      // Step 3: Complete multipart upload
      const completeParams = {
        Bucket: process.env.REACT_APP_GA_BUNDLE_S3_BUCKET,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: {
          Parts: uploadedParts
        }
      };

      const completeCommand = new CompleteMultipartUploadCommand(completeParams);
      await s3Client.send(completeCommand);
      
      console.log(`Successfully uploaded ${file.name} to S3 (multipart)`);
      
      // Clear detailed progress for this file
      setDetailedProgress(prev => {
        const updated = { ...prev };
        delete updated[fileIndex];
        return updated;
      });

    } catch (error) {
      // Abort multipart upload on error
      if (uploadId) {
        try {
          const abortParams = {
            Bucket: process.env.REACT_APP_GA_BUNDLE_S3_BUCKET,
            Key: key,
            UploadId: uploadId
          };
          const abortCommand = new AbortMultipartUploadCommand(abortParams);
          await s3Client.send(abortCommand);
          console.log(`Aborted multipart upload for ${file.name}`);
        } catch (abortError) {
          console.error(`Error aborting multipart upload for ${file.name}:`, abortError);
        }
      }
      
      // Clear detailed progress for this file
      setDetailedProgress(prev => {
        const updated = { ...prev };
        delete updated[fileIndex];
        return updated;
      });
      
      throw error;
    }
  };

  const handleFileUpload = async () => {
    if (files.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setDetailedProgress({});

    try {
      for (let i = 0; i < files.length; i++) {
        await uploadToS3(files[i], i);
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
      alert('Files uploaded successfully!');
      setFiles([]);
    } catch (error) {
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setDetailedProgress({});
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  return (
    <Card className='shadow'>
      <Card.Body>
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`p-4 border border-2 rounded-lg text-center ${
            uploading ? 'bg-light' : 'border-primary'
          }`}
          style={{ borderStyle: 'dashed', minHeight: '200px' }}
        >
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='d-none'
            onChange={handleFileChange}
            disabled={uploading}
          />

          {files.length === 0 ? (
            <div className='py-5'>
              <FontAwesomeIcon
                icon={faCloudUploadAlt}
                size='3x'
                className='text-primary mb-3'
              />
              <h4>Drag and drop files here</h4>
              <p className='text-muted'>or</p>
              <Button
                variant='outline-primary'
                onClick={handleBrowseClick}
                disabled={uploading}
                className='px-4'
              >
                Browse Files
              </Button>
            </div>
          ) : (
            <>
              <h5 className='mb-3'>Selected Files</h5>
              <div className='mb-3'>
                {uploading && (
                  <div className='mb-3'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <small className='text-muted'>Overall Progress</small>
                      <small className='text-muted'>{uploadProgress}%</small>
                    </div>
                    <ProgressBar
                      animated
                      now={uploadProgress}
                      className='mb-3'
                    />
                    
                    {Object.keys(detailedProgress).length > 0 && (
                      <div className='mt-3'>
                        <small className='text-muted mb-2 d-block'>Multipart Upload Progress</small>
                        {Object.values(detailedProgress).map((progress, index) => (
                          <div key={index} className='mb-2'>
                            <div className='d-flex justify-content-between align-items-center mb-1'>
                              <small className='text-truncate me-2' style={{maxWidth: '200px'}}>
                                {progress.fileName}
                              </small>
                              <small className='text-muted'>
                                {progress.uploadedParts}/{progress.totalParts} parts ({progress.progress}%)
                              </small>
                            </div>
                            <ProgressBar
                              variant='info'
                              now={progress.progress}
                              size='sm'
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className='d-flex justify-content-between mb-3'>
                  <Button
                    variant='outline-secondary'
                    onClick={handleBrowseClick}
                    disabled={uploading}
                    size='sm'
                  >
                    Add More Files
                  </Button>
                  <Button
                    variant='primary'
                    onClick={handleFileUpload}
                    disabled={uploading || files.length === 0}
                    className='px-4'
                  >
                    {uploading ? (
                      <>
                        <Spinner
                          animation='border'
                          size='sm'
                          className='me-2'
                        />
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faCloudUploadAlt}
                          className='me-2'
                        />{' '}
                        Upload Files
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className='file-list'>
                {files.map((file, index) => {
                  const isMultipart = file.size >= MULTIPART_THRESHOLD;
                  return (
                    <Card key={index} className='mb-2'>
                      <Card.Body className='py-2 px-3 d-flex justify-content-between align-items-center'>
                        <div>
                          <FontAwesomeIcon
                            icon={faFileZipper}
                            className='me-2 text-primary'
                          />{' '}
                          {file.name}
                          <small className='text-muted ms-2'>
                            {' '}
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </small>
                          {isMultipart && (
                            <small className='badge bg-info ms-2' title='Will use multipart upload'>
                              Multipart
                            </small>
                          )}
                        </div>
                        <Button
                          variant='link'
                          className='text-danger p-1'
                          onClick={() => handleFileRemove(file.name)}
                          disabled={uploading}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
});

export default FileUpload;

